import { DatePipe, DOCUMENT, isPlatformBrowser, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, Injectable, Input, OnChanges, OnInit, PLATFORM_ID, Renderer2, SimpleChanges } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { jobModuleConfig } from '../../../../helpers/jobModule.config';
import { getActiveUrgencyDays } from '../../../../helpers/jobUtils';
import { TruncatePipe } from '../../../../pipes/truncate.pipe';
import { AppSettings } from '../../../../service/AppSettings';
import { GigsumoService } from '../../../../service/gigsumo.service';
import { JobModel } from '../../../../service/job.model';
import { SvgTemplatesComponent } from '../svg-templates/svg-templates.component';
import { getJobParams } from '../job-listing/job-listing.component';


@Injectable({
  providedIn: "root",
})
export class JobProvider {

  private jobdata: any = {};
  private isLoadingJobs: boolean = false;
  private cachedJobResults: any[] = []; // Cache current page results

  // Last job the user viewed - used by the Similar Jobs widget to exclude the job
  // they navigated FROM. Lives on this root singleton so it survives the sidebar
  // being destroyed/recreated across navigations.
  previousJobId: string | null = null;

  // One-shot flag set by the Similar Jobs widget so a click ALWAYS re-calls the
  // Job Details API (findJobsForOpenSession), bypassing the in-cache skip that
  // normal card clicks use. Consumed (reset) on the next paramMap emission.
  forceListingRefresh = false;

  setData(v: any) {
    this.jobdata = v;
  }

  get getJob() {
    return this.jobdata;
  }

  setLoadingState(loading: boolean) {
    this.isLoadingJobs = loading;
    console.log('🔄 JobProvider: setLoadingState =', loading);
  }

  get isLoading() {
    return this.isLoadingJobs;
  }

  // Cache the current page's job results
  setCachedResults(results: any[]) {
    this.cachedJobResults = results || [];
    console.log('💾 JobProvider: Cached', this.cachedJobResults.length, 'job results');
  }

  // Get cached results (persists across component recreations)
  getCachedResults(): any[] {
    return this.cachedJobResults;
  }

  // Check if a specific job exists in cached results
  jobExistsInCache(jobId: string | null): boolean {
    if (!jobId || !this.cachedJobResults || this.cachedJobResults.length === 0) {
      return false;
    }

    const exists = this.cachedJobResults.some((job: any) =>
      job.jobId === jobId ||
      job.id === jobId ||
      job.jobId?.toString() === jobId?.toString()
    );

    console.log('🔍 JobProvider: Job', jobId, exists ? 'EXISTS' : 'NOT FOUND', 'in cache');
    return exists;
  }

}



@Component({
  selector: 'app-job-card',
  standalone: true,
  imports: [NgClass, NgIf, DatePipe, TruncatePipe, RouterLink, SvgTemplatesComponent, NgFor, NgTemplateOutlet],
  templateUrl: './job-card.component.html',
  styleUrl: './job-card.component.scss',
  host: { ngSkipHydration: 'true' }
})
export class JobCardComponent implements OnInit, OnChanges {
  orgData : any = {};
  @Input() view: string = 'small';
  page: number = 1;
  currentQueryParams: any = {};
  constructor(
    private _activateRoute: ActivatedRoute,
    private gigsumoService: GigsumoService,
    public jobData: JobProvider,
    private meta: Meta,
    private title: Title,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object) {

      this.routeSub = this._activateRoute.paramMap.subscribe(params => {
        // URL is now `/jobs/{title-slug}-[{city}-{state}-]{jid}` (hyphens, lowercase).
        // Extract the trailing token; the listing component does the heavier
        // case-correct extraction, but the card just needs a string to compare
        // against `passValues.jobId` for highlighting.
        const slug = params.get('jobId') ?? '';
        this.selectedItem = this.extractTrailingId(slug);
        this.orgData.orgId = params.get('orgId') ?? undefined;
        this.orgData.busId = params.get('businessId') ?? undefined;
      });

      this._activateRoute.queryParams.subscribe(params => {
        this.page = params['page'] ?? 1;
        // Store all query params to preserve them when navigating
        this.currentQueryParams = { ...params };
      });
      
     }
  @Input() passValues: any;
  ENVIRONMENT = environment
  @Input() jobsFoundStatus: any;
  @Input() fromBusiness : boolean = false;
  @Input() loadAPIcall!: Boolean; 
  @Input() responseReceived: any;
  @Input() updateJobModule!: jobModuleConfig;
  jobIdHighLight: any = '';
  @Input('Obj') selectedJob!: JobModel;
  selectedItem: any = '';
  downloadurl: string = AppSettings.photoUrl;
  routeSub = new Subscription
  jobTitle: string = '';
  companyName: string = '';
  location: string = '';
  skills: string = ''
  yearsOfExperience: string = ''
  rateText: string = '';
  ngOnInit(): void {

    if(this.selectedJob){
      this.selectedItem = this.selectedJob.jobId;
    }

    // New design: formatted pay-rate range for the card meta row
    this.rateText = this.gigsumoService.formatRateRange(
      this.passValues?.targetRateFrom,
      this.passValues?.targetRateTo,
      this.passValues?.country,
      this.passValues?.payType
    );

    this.jobTitle = this.passValues.jobTitle
    this.companyName = this.passValues.jobPostedBehalfOf
    this.location = this.passValues.city + ', ' + this.passValues.state
    if (this.passValues && this.passValues.primarySkills) {
      this.passValues.primarySkills.forEach((ele: any, index: number) => {
        if (index == 0) {
          this.skills = this.skills + ele
        } else {
          this.skills = this.skills + ', ' + ele
        }
      })
    }
    this.yearsOfExperience = this.passValues.experienceFrom
    // this.setMetaTags()
    // this.setStructuredData();
  }

  isString(value: any): boolean {
    return typeof value === 'string';
  }
  
  getSkillsArray(): string[] {
    if (!this.passValues || !this.passValues.primarySkills) {
      return [];
    }
    
    if (this.isString(this.passValues.primarySkills)) {
      return this.passValues.primarySkills.split(',');
    }
    
    if (Array.isArray(this.passValues.primarySkills)) {
      return this.passValues.primarySkills;
    }
    
    // If it's an object with a toString method or other type, convert to string
    return [this.passValues.primarySkills.toString()];
  }

  setMetaTags(data: any): void {
    const pageTitle = `${this.passValues.jobTitle} in ${this.passValues.city}, ${this.passValues.state} | Gigsumo,  - ${this.passValues.jobPostedBehalfOf}`;
    const description = `Join ${this.passValues.jobPostedBehalfOf} as a ${this.passValues.jobTitle}. Required skills: ${this.passValues.primarySkills}. Location: ${this.passValues.city}, ${this.passValues.state}. ${this.passValues.experienceFrom}+ years of experience preferred.`;
    this.jobTitle = data.jobTitle;
    this.companyName = data.postedBehalfOf;
    this.location = data.city + ', ' + data.state
    // Set page title
    this.title.setTitle(pageTitle);
    this.meta.addTags([
      { name: 'description', content: `Posted ${data.postedOn} Looking for a ${this.passValues.jobTitle} in ${this.passValues.city}, ${this.passValues.state} with ${this.passValues.primarySkills} and ${this.passValues.experienceTo} years of experience.` },
      { name: 'keywords', content: `${this.passValues.jobTitle}, ${this.passValues.city}, ${this.passValues.state}, ${this.passValues.primarySkills}, ${this.passValues.experienceTo} years experience` },
      { property: 'og:title', content: `${this.passValues.jobTitle} - ${this.passValues.city}, ${this.passValues.state}` },
      { property: 'og:description', content: `A job opportunity for ${this.passValues.jobTitle} with ${this.passValues.primarySkills} skills and ${this.passValues.experienceTo} years of experience.` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://www.gigsumo.com/images/gigsumologo_bigface.png' },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: 'https://www.gigsumo.com/images/gigsumologo_bigface.png' },
      { name: 'twitter:creator', content: '@GigSumoHQ' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { charset: 'UTF-8' },
      { name: 'robots', content: 'index, follow' },
      { name: 'author', content: 'Gigsumo Inc.' }
    ])
    if (isPlatformBrowser(this.platformId)) {
      this.meta.addTags([
        { property: 'og:url', content: window.location.href }
      ])
    }

    setTimeout(() => {
      this.setStructuredData(data)
    }, 300);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if(changes['passValues'].currentValue != changes['passValues'].previousValue) {

    // }
  }

  getStatusClass(status: string): string {
    return this.gigsumoService.getJobStatusClass(status);
  }

  getInitials(fullname: string): string {
    return this.gigsumoService.getInitials(fullname);
  }

  getColor(fullname: string): string {
    return this.gigsumoService.getColor(fullname);
  }

  getActiveUrgencyDays(job: any): number | null {
    return getActiveUrgencyDays(job);
  }

  setJobData(v: any) {
    // console.log("set data" , v);

    this.jobData.setData(v)
  }

  setStructuredData(data: any): void {
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      'title': this.jobTitle,
      'description': `Join ${this.companyName} as a ${this.jobTitle}. Required skills: ${this.skills}. Location: ${this.location}. ${this.yearsOfExperience}+ years of experience preferred.`,
      'identifier': {
        '@type': 'PropertyValue',
        'name': this.companyName,
        'value': '12345'
      },
      'datePosted': new Date().toISOString().split('T')[0], // Current date
      'jobLocation': {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': '123 Tech Street',
          'addressLocality': this.location.split(',')[0],
          'addressRegion': this.location.split(',')[1]?.trim() || '',
          'postalCode': this.passValues.zipCode,
          'addressCountry': this.passValues.country
        }
      },
      'hiringOrganization': {
        '@type': 'Organization',
        'name': this.companyName,
        'sameAs': 'https://www.companywebsite.com'
      },
      'baseSalary': {
        '@type': 'MonetaryAmount',
        'currency': 'US Dollars',
        'value': {
          '@type': 'QuantitativeValue',
          'value': data.targetRateTo,
          'unitText': data.createdOn
        }
      }
    });
    if (isPlatformBrowser(this.platformId)){
      this.renderer.appendChild(document.head, script);
    }
  }


  getRouteURL(jobData: any) {
    // New SEO pattern: /jobs/{title-slug}[-{city}-{state}]-{JID}
    return this.fromBusiness
      ? ['https://app-qa.gigsumo.com/#/auth/login', "/"]
      : ['/jobs', this.gigsumoService.buildJobSlug(jobData)];
  }

  /** Best-effort ID extraction for card highlighting. Tolerates legacy
   *  `_`-separated slugs and the new `-`-separated lowercase form. */
  private extractTrailingId(slug: string): string {
    if (!slug) return '';
    const decoded = decodeURIComponent(slug);

    // New pattern: tokens joined by `-`, ID is the trailing token.
    const trailing = decoded.split(/[-_]/).pop() || '';

    // JID + digits (no hyphen): uppercase prefix to match backend records.
    const jid = (trailing.match(/^jid\d+$/i) || decoded.match(/jid\d+/i) || [])[0];
    if (jid) return jid.toUpperCase();

    // Two-letter alpha prefix + hyphen + digits (JB-…). Last 2 tokens joined.
    const tail2 = decoded.split(/[-_]/).slice(-2).join('-');
    const dashed = (tail2.match(/^[a-z]{2,3}-\d+$/i) || decoded.match(/[a-z]{2,3}-\d+/i) || [])[0];
    if (dashed) return dashed.replace(/^([a-z]{2,3})/i, (m) => m.toUpperCase());

    return trailing;
  }

  getLoginParams(job:any){
     return getJobParams(job);
  }
}
